import { ethers } from 'ethers';

export const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export const isTransientError = (err: unknown): boolean => {
  if (!err || typeof err !== 'object') return false;
  const anyErr: any = err;
  const message = (anyErr.reason || anyErr.message || '').toString().toLowerCase();
  const code = anyErr.code?.toString?.().toLowerCase() || '';

  if (/missing revert data/i.test(message)) return true;
  if (/connection.*reset|econnreset|etimedout|timeout|network error|503|502|econnrefused|socket hang up/i.test(message)) return true;
  if (/circuit breaker/i.test(message)) return true;
  if (/failed to fetch/i.test(message)) return true;
  if (/internal json-rpc error/i.test(message)) return true;
  if (code === 'call_exception' || code === 'servererror' || code === 'networkerror') return true;
  return false;
};

export async function retryWrapper<T>(
  fn: () => Promise<T>,
  attempts = 5,
  baseDelayMs = 300,
): Promise<T> {
  let lastErr: unknown;
  for (let i = 0; i < attempts; i++) {
    try {
      return await fn();
    } catch (err) {
      lastErr = err;
      if (!isTransientError(err) || i === attempts - 1) break;
      const jitter = Math.floor(Math.random() * 200);
      const delay = Math.min(baseDelayMs * 2 ** i + jitter, 5000);
      // eslint-disable-next-line no-console
      console.warn(`Transient RPC error, retrying ${i + 1}/${attempts} after ${delay}ms:`, (err as any)?.message || err);
      await sleep(delay);
    }
  }
  throw lastErr as Error;
}

export async function waitForTx(
  provider: ethers.BrowserProvider,
  txHash: string,
  confirmations = 1,
  timeoutMs = 120000,
) {
  try {
    const receipt = await provider.waitForTransaction(txHash, confirmations, timeoutMs);
    if (receipt) return receipt;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('waitForTransaction failed or timed out, checking receipt directly...', err);
  }

  const pollInterval = 2000;
  const maxTries = Math.ceil(timeoutMs / pollInterval);
  for (let i = 0; i < maxTries; i++) {
    const r = await provider.getTransactionReceipt(txHash);
    if (r && r.blockNumber) return r;
    await sleep(pollInterval);
  }
  throw new Error('Timeout waiting for tx receipt');
}


